/**
 * MockPlayjectorSettings - Configuration for mock Playjector behavior
 *
 * This file allows you to control how the mock simulates various scenarios:
 * - Game launch success/failure
 * - Launch timeouts
 * - Steam login behavior
 * - Error messages
 *
 * Edit these settings to test different scenarios without modifying code.
 *
 * Scope: PlayjectorCoordinator only
 */

export interface MockPlayjectorSettings {
  /**
   * Game Launch Behavior
   */
  launch: {
    /** Enable/disable automatic game launching on campaign switch */
    autoLaunch: boolean;
    /** Delay (ms) before game launches (simulates launch time) */
    launchDelayMs: number;
    /** Should game launch succeed? */
    shouldSucceed: boolean;
    /** Failure reason (used when shouldSucceed = false) */
    failureReason: string;
    /** Timeout (ms) before considering launch failed (0 = no timeout) */
    timeoutMs: number;
  };

  /**
   * Game Session Behavior
   */
  session: {
    /** Auto-close game after this duration (ms). 0 = never auto-close */
    autoCloseDurationMs: number;
  };

  /**
   * Steam Login Behavior
   */
  steam: {
    /** Auto-login when credentials are received? */
    autoLogin: boolean;
    /** Delay (ms) before Steam login completes */
    loginDelayMs: number;
    /** Should Steam login succeed? */
    shouldSucceed: boolean;
  };

  /**
   * Error Simulation
   */
  errors: {
    /** Simulate random errors? */
    enabled: boolean;
    /** Chance of error occurring (0-1, e.g., 0.1 = 10% chance) */
    errorRate: number;
    /** Error types to simulate */
    types: Array<'launch_failure' | 'steam_failure' | 'crash' | 'disconnect'>;
  };

  /**
   * Logging
   */
  logging: {
    /** Enable verbose console logging? */
    verbose: boolean;
  };
}

/**
 * Default settings - Normal operation mode
 *
 * NORMAL MODE:
 * - autoLaunch: true - Game will auto-launch when campaign is assigned
 * - timeoutMs: 0 - No internal Playjector timeout (use campaign timeout instead)
 *
 * To test Playjector timeout (e.g., Steam fails to launch):
 * - Set timeoutMs to 20000 (20 seconds)
 * - Set autoLaunch to false OR set launchDelayMs > timeoutMs
 * - This simulates the real Playjector's internal timeout behavior
 */
export const DEFAULT_MOCK_SETTINGS: MockPlayjectorSettings = {
  launch: {
    autoLaunch: false, // Auto-launch game when campaign assigned
    launchDelayMs: 10*1000, // 10 seconds delay before launch
    shouldSucceed: true,
    failureReason: 'Mock game launch failed (simulated error)',
    timeoutMs: 20000, // 20 seconds
  },
  session: {
    autoCloseDurationMs: 120000, // 2 minutes
  },
  steam: {
    autoLogin: true,
    loginDelayMs: 1000, // 1 second
    shouldSucceed: true,
  },
  errors: {
    enabled: false,
    errorRate: 0,
    types: [],
  },
  logging: {
    verbose: true,
  },
};

/**
 * Testing preset - useful for testing error handling
 */
export const TEST_ERROR_SETTINGS: MockPlayjectorSettings = {
  ...DEFAULT_MOCK_SETTINGS,
  launch: {
    ...DEFAULT_MOCK_SETTINGS.launch,
    shouldSucceed: false,
    failureReason: 'Test: Simulated launch failure',
  },
  errors: {
    enabled: true,
    errorRate: 0.2, // 20% chance
    types: ['launch_failure', 'steam_failure'],
  },
};

/**
 * Timeout testing preset - simulates slow launches
 */
export const TEST_TIMEOUT_SETTINGS: MockPlayjectorSettings = {
  ...DEFAULT_MOCK_SETTINGS,
  launch: {
    ...DEFAULT_MOCK_SETTINGS.launch,
    launchDelayMs: 7000, // 7 seconds (longer than typical timeout)
    timeoutMs: 5000, // 5 second timeout
  },
};

/**
 * Fast testing preset - quick cycles for rapid testing
 */
export const TEST_FAST_SETTINGS: MockPlayjectorSettings = {
  ...DEFAULT_MOCK_SETTINGS,
  launch: {
    ...DEFAULT_MOCK_SETTINGS.launch,
    autoLaunch: true, // Enable auto-launch for tests
    launchDelayMs: 100, // 0.1 seconds
    timeoutMs: 0, // No timeout (let campaign timeout handle it)
  },
  session: {
    autoCloseDurationMs: 0, // No auto-close (tests should explicitly set this if needed)
  },
  steam: {
    ...DEFAULT_MOCK_SETTINGS.steam,
    loginDelayMs: 100, // 0.1 seconds
  },
};

/**
 * Current active settings
 *
 * Change this to switch between presets:
 * - DEFAULT_MOCK_SETTINGS (normal development)
 * - TEST_ERROR_SETTINGS (test error handling)
 * - TEST_TIMEOUT_SETTINGS (test timeout scenarios)
 * - TEST_FAST_SETTINGS (fast testing cycles)
 *
 * Or create your own custom settings object!
 */
export let MOCK_SETTINGS: MockPlayjectorSettings = TEST_FAST_SETTINGS;

/**
 * Update mock settings at runtime
 *
 * @example
 * ```typescript
 * import { updateMockSettings, TEST_ERROR_SETTINGS } from './MockPlayjectorSettings';
 *
 * // Switch to error testing mode
 * updateMockSettings(TEST_ERROR_SETTINGS);
 * ```
 */
export function updateMockSettings(newSettings: Partial<MockPlayjectorSettings>): void {
  MOCK_SETTINGS = {
    ...MOCK_SETTINGS,
    ...newSettings,
    launch: { ...MOCK_SETTINGS.launch, ...newSettings.launch },
    session: { ...MOCK_SETTINGS.session, ...newSettings.session },
    steam: { ...MOCK_SETTINGS.steam, ...newSettings.steam },
    errors: { ...MOCK_SETTINGS.errors, ...newSettings.errors },
    logging: { ...MOCK_SETTINGS.logging, ...newSettings.logging },
  };

  console.log('[MockSettings] Settings updated:', MOCK_SETTINGS);
}

/**
 * Reset to default settings
 */
export function resetMockSettings(): void {
  MOCK_SETTINGS = DEFAULT_MOCK_SETTINGS;
  console.log('[MockSettings] Reset to default settings');
}
